import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FC, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Api } from '../../api'
import { useSwitch } from '../../hooks/switch'
import { useUser } from '../../hooks/user'
import { Form, FormProps } from '../Form'
import { Link } from '../Link'
import { Modal } from '../Modal'

const SignInModal: FC<SignInModalProps> = (props) => {
  const { onRequestSignUp, onFinish, onCancel } = props

  const theme = useTheme()
  const { t } = useTranslation()
  const location = useLocation()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const { signIn } = useUser()
  const { on: opened, close } = useSwitch(true)
  const { on: pending, open: startPending, close: stopPending } = useSwitch()

  const noSignUp = location.pathname.includes('purchase')

  const handleClose = useCallback(() => {
    close()
    onCancel?.()
  }, [close, onCancel])

  const handleSubmit: Required<FormProps>['onSubmit'] = useCallback(
    async (fields) => {
      startPending()

      try {
        const body = await Api.post('member_portal/login', fields)
        close()
        await signIn({
          memberId: body.member_id,
          leadId: body.lead_id,
          userId: body.user_id,
          email: fields.email,
          token: body.token,
          firstName: body.first_name,
          lastName: body.last_name,
          gymId: Number(body.gym_id),
        })
        onFinish?.()
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      } finally {
        stopPending()
      }
    },
    [close, onFinish, signIn, startPending, stopPending]
  )

  return (
    <Modal
      open={opened}
      maxWidth={noSignUp ? 'xs' : undefined}
      onClose={handleClose}
    >
      <Grid
        container
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        spacing={5}
      >
        <Grid size={{ sm: 'grow' }}>
          <Box mb={1}>
            <Typography variant="h1">{t('Sign in')}</Typography>
          </Box>
          <Typography>
            {t('Already have an account? Sign in to continue your booking.')}
          </Typography>
          <Form onSubmit={handleSubmit}>
            <TextField
              label={t('Email')}
              type="email"
              name="email"
              required
              autoFocus
            />
            <TextField
              label={t('Password')}
              type="password"
              name="password"
              required
            />
            <Typography fontWeight={600} textAlign="end">
              <Link
                href={`${process.env.REACT_APP_WEB_PORTAL_ORIGIN}/auth/reset-password`}
                target="_blank"
              >
                {t('Forgot your password?')}
              </Link>
            </Typography>
            <Box mt={3}>
              <Button type="submit" loading={pending} fullWidth={downSm}>
                {t('Sign in')}
              </Button>
            </Box>
          </Form>
        </Grid>
        {!noSignUp && (
          <>
            <Divider
              {...(!downSm && { orientation: 'vertical', flexItem: true })}
            />
            <Grid size={{ sm: 'grow' }}>
              <Box mb={1}>
                <Typography variant="h1">{t('Sign up')}</Typography>
              </Box>
              <Typography>
                {t('No account yet? Sign up to get started with your booking.')}
              </Typography>
              <Box mt={3}>
                <Button fullWidth={downSm} onClick={onRequestSignUp}>
                  {t('Sign up')}
                </Button>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  )
}

type SignInModalProps = {
  onRequestSignUp: VoidFunction
  onFinish?: VoidFunction
  onCancel?: VoidFunction
}

export { SignInModal }
export type { SignInModalProps }
